import { getSocket } from 'src/CrudManager/Core/Utils/echoSocket'
import { i18n } from 'src/CrudManager/Core/Utils/i18n'
import { showErrorNotification, showSuccessNotification, showWarningNotification } from 'src/CrudManager/Core/Utils/ToastNotification'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { Dialog } from 'quasar'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import { externalEditorRouteFor } from 'src/CrudManager/Core/composables/CrudUtils/externalEditorRoutes'
import useSharedCrud from 'src/CrudManager/Core/composables/CrudUtils/useSharedCrud'
import CrudManagerDialog from 'src/CrudManager/Core/composites/Layout/CrudManager.Dialog.vue'
import { useI18nStore } from 'src/CrudManager/Core/stores/useI18nStore'
import type {
  CrudInstance,
  CrudInstanceProps,
  CrudManagerModuleName,
  CrudModel,
  CrudModule,
  CrudObject,
  ModelMetadata,
} from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { availableModulesList, getModelMetadata, loadModel, modelRegistry } from 'src/CrudManager/CrudModels/generated-crud'
import { Router } from 'src/router/index' // This works because we are in a quasar project
import { ref, shallowRef } from 'vue'

const availablesModules = shallowRef<CrudModule[]>([])
const availablesModels = shallowRef<ModelMetadata[]>([]) // Now stores lightweight metadata
const isInitiated = ref(false)
const mainMenuIsMini = useLocalStorage('crudManager.mainMenuIsMini', false)
const leftDrawerOpen = useLocalStorage('crudManager.leftDrawerOpen', true)
const activeSidebarCategory = useLocalStorage<string>('crudManager.activeSidebarCategory', 'operations')
const lastClosedInstance = ref<CrudInstance | null>(null)
const instanceCreationInProgress = new Map<string, Promise<CrudInstance | null>>()

export const useCrudManagerStore = defineStore(
  'CrudManager',
  () => {
    // Use useLocalStorage to automatically persist and restore instances
    const crudInstances = useLocalStorage<CrudInstance[]>('crudManager.instances', [])

    const {
      getModuleConfig,
      getInstanceById,
      getModule,
      getModelMeta,
      getModuleBySlug,
      getModelBySlug,
      getModelComponent,
      redirectToLister,
      instanceExists,
      openEditor,
      generateModuleUrl,
    } = useCrudUtils({ router: Router, i18n })

    /**
     * Applies the URL-query prefill onto an already-existing creator draft, the way
     * bootCreatorInstance would have on a fresh one.
     */
    const reseedCreatorPrefill = (instance: CrudInstance, props: CrudInstanceProps | null) => {
      if (instance.moduleName !== 'creator') return

      const prefill = (props?.queryParams ?? {}) as Record<string, unknown>
      if (Object.keys(prefill).length === 0) return

      const instanceData = instance.data as { item?: Record<string, unknown> } | undefined
      if (instanceData?.item) Object.assign(instanceData.item, prefill)
    }

    const createInstance = (modelName: string, moduleName: string, props: CrudInstanceProps | null = null, parentInstanceId = '', makeVisible = true) => {
      // A model whose editor was replaced by an external screen carries no
      // editor config anymore. openEditor already diverts; this catches the
      // callers that reach the store directly — the creator's "open the new
      // record" path — which otherwise persist a config-less pane that restores
      // as a permanently blank editor on every /manage route.
      const externalRoute = externalEditorRouteFor(modelName)
      if (moduleName === 'editor' && externalRoute) {
        if (makeVisible && props?.modelId && Router) {
          void Router.push(externalRoute(String(props.modelId)))
        }
        return Promise.resolve(null)
      }

      const instanceKey = `${modelName}-${moduleName}-${props?.modelId || ''}`

      if (instanceCreationInProgress.has(instanceKey)) {
        return instanceCreationInProgress.get(instanceKey)
      }

      const creationPromise = new Promise<CrudInstance | null>((resolve, reject) => {
        const existingInstance = instanceExists(modelName, moduleName, props?.modelId as string)
        if (existingInstance) {
          // A creator carries no modelId, so any lingering draft matches here and the
          // caller's prefill would never reach boot. Re-seed it instead: opening "new
          // service call" from a client fiche must land on that client, not on whoever
          // the abandoned draft was for. No prefill given (the lister's + button) leaves
          // the draft untouched.
          reseedCreatorPrefill(existingInstance, props)
          displayInstance(existingInstance, makeVisible)
            .then(() => {
              // Update route if instance should be visible and is not a dialog
              if (makeVisible && !existingInstance.status.useDialog && !existingInstance.status.isDialog) {
                if (existingInstance.moduleName === 'creator' || existingInstance.moduleName === 'editor') {
                  const url = generateModuleUrl(existingInstance.modelName, existingInstance.moduleName, existingInstance.props?.modelId as string)
                  if (url && Router) {
                    Router.push(url)
                  }
                }
              }
              resolve(existingInstance)
            })
            .catch(reject)
          return
        }

        // Get model metadata first (lightweight)
        const modelMeta = getModelMetadata(modelName)
        const module = getModule(moduleName)

        if (!modelMeta) {
          console.error(i18n.t('crudManager.errors.modelNotFound', { modelName: modelName }))
          return reject(new Error(`Model not found: ${modelName}`))
        }

        if (!module) {
          console.error(i18n.t('crudManager.errors.moduleNotFound', { moduleName: moduleName }))
          return reject(new Error(`Module not found: ${moduleName}`))
        }

        const settings = getModuleConfig(modelName, moduleName as CrudManagerModuleName)
        const mergedProps = props ? { ...settings, ...props } : settings

        const isPinned = props?.isPinned ?? false
        const useDialog = props?.useDialog ?? false

        if (moduleName === 'editor' || moduleName === 'creator') {
          const listerInstance = crudInstances.value.find((item) => item.modelName === modelName && item.moduleName === 'lister')
          if (listerInstance) {
            parentInstanceId = listerInstance.id
            continueInstanceCreation()
          } else {
            createInstance(modelName, 'lister', null, null, true)
              .then((newListerInstance) => {
                if (newListerInstance) {
                  parentInstanceId = newListerInstance.id
                  continueInstanceCreation()
                }
              })
              .catch(reject)
            return
          }
        } else {
          continueInstanceCreation()
        }

        async function continueInstanceCreation() {
          const nextPosition = crudInstances.value.length > 0 ? crudInstances.value[crudInstances.value.length - 1].position + 1 : 0

          const newId = crypto.randomUUID()

          // For creator, we might need default item data, so load the specific config if needed
          let defaultItemData = {}
          if (module && module.name === 'creator') {
            try {
              const creatorConfig = await getModelConfig(modelName, 'creator')
              if (creatorConfig) {
                const config = creatorConfig as { defaultItemData?: () => Record<string, unknown> }
                defaultItemData = config.defaultItemData?.() || {}
              }
            } catch (error) {
              console.warn(`Could not load default item data for ${modelName}:`, error)
            }
          }

          const newInstanceObject: CrudInstance = {
            id: newId,
            modelName: modelName,
            moduleName: module.name,
            openedAt: null,
            currentlyDisplayed: null,
            status: {
              isLoading: true,
              isSaving: false,
              isAwaitingSave: false,
              isVisible: false,
              isPinned,
              useDialog,
              shouldAnimate: false,
            },
            data: {
              id: newId,
              item: defaultItemData,
              originalItem: {},
            },
            props: mergedProps as CrudInstanceProps,
            width: 0,
            height: 0,
            position: nextPosition,
            parentId: parentInstanceId,
            instanceClass: '',
            title: '',
            subtitle: '',
          }

          const bootedInstance = await module.boot(newInstanceObject)

          if (bootedInstance.status.useDialog) {
            // Add the instance to crudInstances first but mark it as a dialog
            bootedInstance.status.isDialog = true // Add this property to prevent URL changes
            crudInstances.value.push(bootedInstance)

            Dialog.create({
              component: CrudManagerDialog,
              componentProps: {
                instanceId: bootedInstance.id,
                title: i18n.t('crudManager.dialog.edit', { model: i18n.t(`crudmodels.${modelMeta.name}.name`, 1) }),
              },
            })
              .onOk(() => {
                resolve(bootedInstance)
              })
              .onCancel(() => {
                // Remove the instance when dialog is cancelled
                removeInstance(bootedInstance, false)
                resolve(null)
              })
          }
          try {
            await displayInstance(bootedInstance, makeVisible)
            crudInstances.value.push(bootedInstance)

            // Update route if instance should be visible and is not a dialog
            if (makeVisible && !bootedInstance.status.useDialog && !bootedInstance.status.isDialog) {
              if (bootedInstance.moduleName === 'creator' || bootedInstance.moduleName === 'editor') {
                const url = generateModuleUrl(bootedInstance.modelName, bootedInstance.moduleName, bootedInstance.props?.modelId as string)
                if (url && Router) {
                  Router.push(url)
                }
              }
            }

            resolve(bootedInstance)
          } catch (error) {
            reject(error)
          }
        }
      }).finally(() => {
        instanceCreationInProgress.delete(instanceKey)
      })

      instanceCreationInProgress.set(instanceKey, creationPromise)
      return creationPromise
    }

    /**
     * Boots all restored instances from localStorage that haven't been booted yet
     */
    const bootRestoredInstances = async () => {
      for (const instance of crudInstances.value) {
        const module = getModule(instance.moduleName)
        if (!module || !module.boot) continue

        try {
          // Boot the instance - this ensures creator instances get their default data
          await module.boot(instance)
        } catch (error) {
          console.error(`Failed to boot restored instance ${instance.id}:`, error)
        }
      }
    }

    /**
     * The recents hook for screens living outside CrudManager: stamps (or
     * re-stamps) a lightweight 'external' instance so the visit shows in the
     * recents UIs. It never renders as a pane — no module exists for it — and
     * its URL resolves through the external-route registry (generateModuleUrl).
     */
    const touchExternalRecent = (modelName: string, modelId: string, labels: { title?: string; subtitle?: string } = {}) => {
      const stamp = new Date().toISOString() as unknown as CrudInstance['openedAt']

      const existing = crudInstances.value.find(
        (instance) => instance.moduleName === 'external' && instance.modelName === modelName && instance.props?.modelId === modelId,
      )
      if (existing) {
        existing.openedAt = stamp
        if (labels.title) existing.title = labels.title
        if (labels.subtitle !== undefined) existing.subtitle = labels.subtitle
        return existing
      }

      const instance: CrudInstance = {
        id: crypto.randomUUID(),
        openedAt: stamp,
        modelName,
        moduleName: 'external',
        props: { modelId },
        status: { isLoading: false, isSaving: false, isAwaitingSave: false, isVisible: false, isPinned: false },
        width: 0,
        height: 0,
        position: crudInstances.value.length,
        parentId: '',
        currentlyDisplayed: null,
        title: labels.title ?? '',
        subtitle: labels.subtitle ?? '',
        instanceClass: '',
      }
      crudInstances.value.push(instance)

      // recents, not history: the stamp list stays bounded
      const externals = crudInstances.value.filter((candidate) => candidate.moduleName === 'external')
      if (externals.length > 20) {
        const oldestFirst = externals
          .slice()
          .sort((a, b) => new Date(a.openedAt || 0).getTime() - new Date(b.openedAt || 0).getTime())
        const drop = new Set(oldestFirst.slice(0, externals.length - 20).map((candidate) => candidate.id))
        crudInstances.value = crudInstances.value.filter((candidate) => !drop.has(candidate.id))
      }
      return instance
    }

    const init = async () => {
      if (!isInitiated.value) {
        // Load lightweight modules (small memory footprint)
        availablesModules.value = availableModulesList.filter((module) => module && !module.disabled) as CrudModule[]

        // Load lightweight model metadata (no heavy configs)
        const i18nStore = useI18nStore()

        if (availablesModels.value.length === 0) {
          const processedModels = new Set<string>()

          modelRegistry.forEach((modelMeta) => {
            if (!modelMeta || !modelMeta.name) return
            if (!processedModels.has(modelMeta.name)) {
              processedModels.add(modelMeta.name)
              // Add dynamic slug property
              const enhancedMeta = {
                ...modelMeta,
                slug: i18nStore.getModelString(modelMeta.name, 'slug'),
              }
              availablesModels.value.push(enhancedMeta)
            } else {
              console.warn(`Duplicate model found: ${modelMeta.name}`)
            }
          })
        }

        // Initialize socket listeners after model metadata is loaded
        initSocketListeners()

        // Partner editor panes persisted before the tab editor was deleted
        // would restore as blank, titled panes with no tabs — drop them here,
        // once, instead of booting them.
        crudInstances.value = crudInstances.value.filter(
          (instance) =>
            !(instance.moduleName === 'editor' && (instance.modelName === 'business_partner' || instance.modelName === 'supplier')),
        )

        // Boot all restored instances before creating new ones
        await bootRestoredInstances()

        const moduleQuery = Router.currentRoute.value.query.module as string
        const modelQuery = Router.currentRoute.value.query.model as string
        isInitiated.value = true

        // Create instances sequentially to ensure proper visibility control
        // Only process non-disabled models
        for (const modelMeta of availablesModels.value) {
          const isAlreadyDisplayed = crudInstances.value.find(
            (i) => i.modelName === modelMeta.name && (i.moduleName === 'lister' || i.moduleName === 'creator'),
          )
          if (isAlreadyDisplayed || modelMeta.disabled) continue

          // Only show the instance if it matches the current route query
          const shouldShow = moduleQuery === 'lister' && modelQuery === modelMeta.name
          await createInstance(modelMeta.name, 'lister', null, '', shouldShow)
        }
      }
    }

    /**
     * Loads a model configuration on demand
     * @param modelName - Name of the model to load
     * @returns Promise resolving to the full model configuration
     */
    const getFullModel = async (modelName: string): Promise<CrudModel<CrudObject>> => {
      return loadModel(modelName)
    }

    /**
     * Loads a model configuration for the specific model name and module names
     * @param modelName - The name of the model to load the configuration for
     * @param moduleName - The name of the module to load the configuration for
     * @returns The model configuration for the specific model name and module names
     */
    const getModelConfig = async (modelName: string, moduleName: string): Promise<unknown> => {
      const model = await getFullModel(modelName)
      return model?.config[moduleName]
    }

    const initSocketListeners = () => {
      const socket = getSocket()

      if (!socket) {
        console.warn('Socket not initialized')
        return
      }

      socket.private(`CrudModels`).listen('.App\\Packages\\CrudManager\\Events\\CrudDeleted', (data: { id: string; modelName: string }) => {
        if (!data.modelName || !data.id) {
          console.error('Missing required data:', { modelName: data.modelName, id: data.id })
          return
        }
        const instance = crudInstances.value.find((inst) => inst.data?.item?.id === data.id && inst.modelName === data.modelName)
        if (instance) {
          removeInstance(instance)
          showWarningNotification("L'instance a été supprimée, elle as donc été fermée!")
        }
      })
    }

    const removeInstance = async (instance: CrudInstance, displayLister = true) => {
      // First mark the instance as not visible
      instance.status.isVisible = false
      lastClosedInstance.value = instance

      // Remove the instance and its children
      const removeInstanceAndChildren = (id: string) => {
        const index = crudInstances.value.findIndex((item) => item.id === id)
        if (index !== -1) {
          crudInstances.value.splice(index, 1)
        }

        const children = crudInstances.value.filter((item) => item.parentId === id)
        children.forEach((child) => removeInstanceAndChildren(child.id))
      }

      // Check if we need to redirect before removing the instance
      const needsRedirect = instance.moduleName === 'editor' || instance.moduleName === 'creator'
      const modelName = instance.modelName

      // Remove the instance and its children
      removeInstanceAndChildren(instance.id)

      // Check if there are any remaining instances of the same model
      const hasOtherInstances = crudInstances.value.some((inst) => inst.modelName === modelName && inst.moduleName === 'editor' && inst.id !== instance.id)

      // Handle redirection after instance removal
      if (needsRedirect || !hasOtherInstances) {
        // Find or ensure a lister instance exists
        const existingLister = crudInstances.value.find((item) => item.modelName === modelName && item.moduleName === 'lister')

        if (displayLister) {
          if (existingLister) {
            // If lister exists, make it visible before redirecting
            existingLister.status.isVisible = true
            redirectToLister(modelName)
          } else {
            // If no lister exists, create one and then redirect
            await createInstance(modelName, 'lister', null, null, true)
            redirectToLister(modelName)
          }
        }
      }
    }

    const displayInstance = async (instance: CrudInstance, makeVisible = true): Promise<void> => {
      if (!instance) {
        throw new Error('Instance is required')
      }

      // Set visibility immediately if requested
      if (makeVisible) {
        // set status.isVisible on all other instances to false
        crudInstances.value.forEach((i) => (i.status.isVisible = false))

        instance.status.isVisible = true
        instance.openedAt = new Date().toISOString()
      }

      instance.status.isLoading = true

      try {
        const { getFromDB } = useSharedCrud(instance)
        if (instance.moduleName !== 'creator') {
          const success = await getFromDB()

          // If getFromDB returns false, it means an error occurred and was handled
          // The error dialog will handle instance removal, so we should return early
          if (!success) {
            return
          }
        }

        // Update instance title and subtitle after data is loaded
        // Load specific module config only when we need title/subtitle configuration
        try {
          const moduleConfig = await getModelConfig(instance.modelName, instance.moduleName)
          if (moduleConfig && instance.data?.item) {
            const config = moduleConfig as {
              getTitle?: (item: unknown) => string | Promise<string>
              getSubtitle?: (item: unknown) => string | Promise<string>
            }
            if (config.getTitle) {
              const titleResult = config.getTitle(instance.data.item)
              // Handle both sync and async getTitle functions
              instance.title = titleResult instanceof Promise ? await titleResult : titleResult
            }
            if (config.getSubtitle) {
              const subtitleResult = config.getSubtitle(instance.data.item)
              // Handle both sync and async getSubtitle functions
              instance.subtitle = subtitleResult instanceof Promise ? await subtitleResult : subtitleResult
            }
          }
        } catch (error) {
          console.warn(`Could not load model config for titles: ${instance.modelName}`, error)
          // Fallback to basic title
          instance.title = instance.modelName
        }

        // Trigger animation after data is loaded
        instance.status.shouldAnimate = true
        setTimeout(() => {
          instance.status.shouldAnimate = false
        }, 500)

        lastClosedInstance.value = null
        instance.status.isLoading = false
      } catch (error) {
        console.error('Error loading instance data:', error)
        instance.status.isLoading = false
        throw error
      }
    }

    const toggleVisible = (instance: CrudInstance) => {
      if (instance.status.isVisible) {
        instance.status.isVisible = false
        if (instance.parentId) {
          const parentInstance = getInstanceById(instance.parentId)
          if (parentInstance) {
            toggleVisible(parentInstance)
          }
        }
      } else {
        mainMenuIsMini.value = false
        crudInstances.value.forEach((ins) => {
          if (instance.id !== ins.id) {
            ins.status.isVisible = false
          }
        })
        instance.status.isVisible = true
      }
    }
    const resetInstance = (instance: CrudInstance) => {
      if (!instance) return
      const module = getModule(instance.moduleName)

      if (!module || !module.reset) return

      module.reset(instance)
    }

    const saveInstance = async (instance: CrudInstance) => {
      const module = getModule(instance.moduleName)

      if (!module || !module.save) return
      const saveFn = module.save
      if (saveFn) {
        try {
          const result = await saveFn(instance)

          if (result) {
            instance.status.isAwaitingSave = false
            showSuccessNotification('Sauvegarde réussie')
          } else {
            showSuccessNotification('Sauvegarde réussie')
          }
        } catch (error) {
          console.error(error)
          showErrorNotification('Erreur lors de la sauvegarde')
        }

        instance.status.isAwaitingSave = false
      } else showErrorNotification('Fonction manquante sur le module')
    }

    return {
      isInitiated,
      availablesModels,
      toggleVisible,
      getModule,
      getModelMeta, // Lightweight metadata lookup
      getFullModel, // Lazy-loaded full configuration
      getModelConfig, // Get specific module configuration
      getModelComponent,
      crudInstances,
      mainMenuIsMini,
      displayInstance,
      createInstance,
      touchExternalRecent,
      lastClosedInstance,
      removeInstance,
      availablesModules,
      resetInstance,
      saveInstance,
      init,
      getModuleBySlug,
      getModelBySlug,
      leftDrawerOpen,
      activeSidebarCategory,
      openEditor,
    }
  },
  {
    persist: {
      pick: [],
      afterHydrate: (context) => {
        context.store.init()
      },
    },
  },
)
